import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { firstValueFrom } from 'rxjs';
import posthog from 'posthog-js';
import { TokenService } from '../token/token.service';
import { FoodDataService } from 'src/app/modules/food/services/food-data/food-data.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PosthogService {

  constructor(
    private tokenService: TokenService,
    private subsDataService: FoodDataService,
  ) {
    this.registerDeviceProperties();
    this.registerLifecycleListeners();
  }

  /**
   * Registra le proprietà del device come super properties di PostHog
   * così vengono inviate con ogni evento e PostHog riconosce l'app correttamente
   */
  private async registerDeviceProperties() {
    const isNative = Capacitor.isNativePlatform();

    try {
      const info = await Device.getInfo();
      posthog.register({
        $os: info.operatingSystem,
        $os_version: info.osVersion,
        $device_type: isNative ? 'Mobile' : 'Desktop',
        $device_model: info.model,
        $device_manufacturer: info.manufacturer,
        $app_version: environment.appVersion,
        is_native_app: isNative,
      });
    } catch (e) {
      posthog.register({
        $device_type: 'Desktop',
        is_native_app: false,
      });
    }
  }

  /**
   * Registra i listener per i lifecycle events dell'app (solo su piattaforma nativa)
   * Emette 'Application Opened' e 'Application Backgrounded' come gli SDK nativi PostHog
   */
  private registerLifecycleListeners() {
    if (!Capacitor.isNativePlatform()) return;

    App.addListener('appStateChange', ({ isActive }) => {
      posthog.capture(isActive ? 'Application opened' : 'Application backgrounded');
    });
  }

  /**
   * Identifica l'utente su PostHog con i dati del profilo
   */
  identify(idAccount: string, properties?: Record<string, any>) {
    posthog.identify(idAccount, properties);
  }

  /**
   * Resetta l'utente su PostHog (da chiamare al logout)
   */
  reset() {
    posthog.reset();
  }

  /**
   * Registra proprietà globali che verranno inviate con ogni evento
   */
  register(properties: Record<string, any>) {
    posthog.register(properties);
  }

  /**
   * Traccia una schermata visualizzata
   */
  captureScreen(screenName: string) {
    posthog.capture('$screen', { $screen_name: screenName });
  }

  /**
   * Converte un URL Angular in un nome schermata leggibile
   * Es. '/tabs/my-pet/animale/patologie?idAnimale=1' → 'MyPet > Animale > Patologie'
   */
  screenNameFromUrl(url: string): string {
    const path = url.split('?')[0]; // rimuove query params
    const segments = path.split('/').filter(s => s && s !== 'tabs');

    return segments
      .map(s => s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
      .join(' > ');
  }

  /**
   * Traccia un evento su PostHog con contesto utente e device
   * @param eventName Il nome dell'evento, es. 'login', 'pet_aggiunta_terapie'
   * @param extraParams Parametri aggiuntivi specifici dell'evento
   */
  async capture(eventName: string, extraParams?: Record<string, unknown>) {
    const token = await firstValueFrom(this.tokenService.getToken());
    const accountId = token?.payload.idAccount ?? null;

    let deviceId = '';
    let deviceInfo: Record<string, string | undefined> = {};
    try {
      deviceId = (await Device.getId()).identifier || '';
      const info = await Device.getInfo();
      deviceInfo = {
        deviceName: info.name || undefined,
        deviceModel: info.model || undefined,
        devicePlatform: info.platform || undefined,
        deviceOS: info.operatingSystem || undefined,
        deviceOSVersion: info.osVersion || undefined,
        deviceManufacturer: info.manufacturer || undefined,
      };
    } catch (e) {
      console.warn('Impossibile recuperare info device:', e);
    }

    let hasActiveSubscription = 'no';
    try {
      const subscriptions = await this.subsDataService.getAll();
      hasActiveSubscription = subscriptions.some(sub => sub.idStato === 2) ? 'yes' : 'no';
    } catch {}

    let accountData: Record<string, string | undefined> = {};
    if (token) {
      try {
        const response = await fetch(
          environment.apiUrl + environment.apiVersion + '/accounts/read',
          { headers: { Authorization: token.toString() } }
        );
        const data = await response.json();
        if (response.ok && data.body) {
          accountData = {
            accountName: data.body.nome,
            accountSurname: data.body.cognome,
            accountCF: data.body.codiceFiscale,
            accountEmail: data.body.email,
            accountPhone: data.body.telefono,
          };
        }
      } catch (e) {
        console.warn("Impossibile recuperare i dati dell'utente:", e);
      }
    }

    posthog.capture(eventName, {
      accountId,
      deviceId,
      hasActiveSubscription,
      ...deviceInfo,
      ...accountData,
      ...extraParams,
    });
  }
}
